import { z } from 'zod';
import { queueCountsSchema } from './jobs.js';
import { PLAN_RUN_STATUSES } from './plan-runs.js';
/**
 * AI operations.
 *
 * Everything here is an aggregate over a rolling window rather than a list:
 * "what did coach chat cost this month" is the question, and a page of
 * individual calls answers it badly.
 *
 * **No prompt or completion text appears anywhere in this file, and none is
 * stored.** Steado's prompts carry the user's conditions, medications and
 * allergies; design spec §5 makes `GET /admin/users/:id/health` the only route
 * to those — role `admin`, audited on every read — and a screen that listed
 * prompts would route around that gate entirely. See the note on `LlmCall` in
 * prisma/schema.prisma.
 */
/** Mirrors Prisma's LlmCallTask, and the router's own `LlmTask` union. */
export const LLM_TASKS = [
    'plan_generation',
    'coach_chat',
    'memory_extraction',
    'food_decomposition',
    'vision_scan',
    /** Unit 7a's — one per coach turn, at the cheap tier. */
    'safety_classification',
    /** One user-facing cooking guide, written once per food and stored. */
    'recipe_guide',
    /** One rest-day coaching tip, written per user at the cheap tier. */
    'coaching_tip',
    /** One user's weekly review prose, written once a week for every tier. */
    'weekly_review',
    /** The coach's read of a day or a month on the History screens, for every tier. */
    'coach_read',
    /** The chips under a coach reply, one call per turn at the cheap tier. */
    'coach_follow_ups',
    'scan_router',
    'equipment_scan',
    'exercise_draft',
    /** The strong model researching a food request the system filed, with web search. */
    'food_request_research',
    /** Which catalog food a typed or scanned dish is, when its names could not settle it. */
    'food_match',
    /** A user's active memories condensed into the note the coach reads, rebuilt in the background. */
    'memory_summary',
    /** A gym member's "what stands out" and trainer brief, written once per member per plan week. */
    'gym_insight',
    /** One model round of a trainer's or an owner's Ask in the gym apps. */
    'gym_ask',
    /** Coach's take on one workout, once per workout whose facts changed, for every tier. */
    'activity_take',
    /**
     * One embeddings request (memory recall, a stored fact). The only task the
     * chat router never sends: its model is EMBEDDING_MODEL.
     */
    'embedding',
];
/**
 * Mirrors Prisma's LlmCallStatus. Two values on purpose: this is the outcome of
 * the HTTP call, not of the answer. Whether the model said something usable is
 * recorded per task — `PlanGenerationRun.status`, `FoodScan.failureReason`.
 */
/**
 * `timeout` is its own outcome: a request we stopped waiting for is still
 * generated and billed, so its cost is unknown where a refusal's is zero.
 */
export const LLM_CALL_STATUSES = ['succeeded', 'provider_error', 'timeout'];
/** The rolling window every panel on this screen reads. */
export const AI_WINDOW_DAYS = 30;
export const AI_MAX_WINDOW_DAYS = 90;
export const adminAiWindowQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(AI_MAX_WINDOW_DAYS).default(AI_WINDOW_DAYS),
});
/**
 * Spend that may be an undercount, and says so.
 *
 * Cost is OpenRouter's own figure, requested per call — never derived from a
 * price table in this repo, which would go stale silently and produce numbers
 * that look authoritative. The provider does not always report one, so a total
 * is a floor rather than a total, and `costIsPartial` is how the screen can say
 * "at least this much" instead of quietly rounding the gap to zero.
 */
export const adminAiSpendSchema = z.object({
    calls: z.number().int(),
    failures: z.number().int(),
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    /** Prompt tokens the provider served from its cache — the breakpoint working. */
    cachedInputTokens: z.number().int(),
    cacheWriteTokens: z.number().int(),
    /**
     * Null only when every call in scope has an unknown cost. A scope with no
     * calls is 0, and a failed call counts as free: the provider refused it.
     */
    costUsd: z.number().nullable(),
    /**
     * True when a call succeeded or timed out without reporting a cost — the
     * sum is then a floor. A provider refusal does not set it.
     */
    costIsPartial: z.boolean(),
    /** Null when there were no successful calls to average. */
    avgLatencyMs: z.number().int().nullable(),
});
/** A model actually observed running a task, as opposed to the one configured. */
export const adminAiModelUsageSchema = z.object({
    model: z.string(),
    calls: z.number().int(),
    costUsd: z.number().nullable(),
});
export const adminAiTaskUsageSchema = adminAiSpendSchema.extend({
    task: z.enum(LLM_TASKS),
    /**
     * What the router would send this task to right now, read from the running
     * process's own configuration. Shown beside what actually ran: an env
     * override that never took effect is invisible from either half alone.
     */
    configuredModel: z.string(),
    /**
     * Every model the router may legitimately send this task to right now,
     * `configuredModel` first. One entry for most tasks; plan generation lists
     * its primary, repair and escalation tiers, and a fallback chain can serve
     * a call from any of them. A served model outside this list is the one
     * worth a warning.
     */
    expectedModels: z.array(z.string()).min(1),
    /** Every model seen running this task in the window, busiest first. */
    models: z.array(adminAiModelUsageSchema),
});
/**
 * Who wrote the weekly reviews generated in the window.
 *
 * `llm_calls` can say a `weekly_review` call failed at the provider; it cannot
 * say a call that succeeded was thrown away because its prose stated a number
 * the facts did not contain. That rejection is the one worth watching, and it
 * only exists on the review row (`WeeklyReview.proseSource`). Counts only: no
 * prose reaches this screen.
 */
export const adminAiWeeklyReviewsSchema = z.object({
    /** The model's prose, kept. */
    model: z.number().int(),
    /** The model was asked and its answer failed or was rejected; code wrote it instead. */
    fallback: z.number().int(),
    /** Code prose by design: the user has not agreed to AI processing, so no call was made. */
    noAiConsent: z.number().int(),
    /** Weeks with nothing measured, which have no prose at all. */
    nothingMeasured: z.number().int(),
});
export const adminAiUsageSchema = z.object({
    windowDays: z.number().int(),
    /** Inclusive lower bound of the window, so the screen can name its own scope. */
    from: z.string(),
    totals: adminAiSpendSchema,
    /** One row per task the router knows about, including tasks with no calls. */
    tasks: z.array(adminAiTaskUsageSchema),
    weeklyReviews: adminAiWeeklyReviewsSchema,
});
// --- Food scan coverage -----------------------------------------------------
/**
 * How the vision + decomposition cascade is doing.
 *
 * `FoodScan.ingredientCoverage` and `unresolvedIngredients` were already being
 * written and read by nobody but the app; this is the aggregation over them. No
 * new storage — the numbers have been there since food scans shipped.
 */
export const adminAiUnresolvedIngredientSchema = z.object({
    name: z.string(),
    /** Scans in the window whose decomposition failed to match this ingredient. */
    occurrences: z.number().int(),
});
/**
 * A dish the model sizes badly, ranked by how badly.
 *
 * Every confirmed scan carries the model's estimate next to the grams the user
 * settled on, so this is a review queue built from corrections users already
 * made rather than from anything new we ask them for. Human review of the top
 * of this list is how the catalog's named portions get fixed — a dish that is
 * consistently 40% low usually means a missing or wrong `FoodPortion`.
 */
export const adminAiPortionOffenderSchema = z.object({
    dishName: z.string(),
    corrections: z.number().int(),
    /** Median grams the model guessed, and median grams users actually logged. */
    medianEstimatedGrams: z.number(),
    medianConfirmedGrams: z.number(),
    /**
     * Median signed error as a share of the confirmed portion. Negative means the
     * model reads the dish SMALLER than it is, which is the direction that
     * silently undercounts someone's day.
     */
    medianRelativeError: z.number(),
    /** Whether the learned prior is live for this dish yet. */
    priorApplied: z.boolean(),
});
export const adminAiCoverageSchema = z.object({
    windowDays: z.number().int(),
    from: z.string(),
    scans: z.object({
        total: z.number().int(),
        completed: z.number().int(),
        failed: z.number().int(),
        /** Resolved by the catalog (tier 1) rather than by decomposition (tier 3). */
        matchedFromCatalog: z.number().int(),
        decomposed: z.number().int(),
    }),
    /**
     * Coverage is recorded only for decomposed scans, so this is scoped to those
     * — averaging it over catalog matches would report a number about a
     * population that has none.
     */
    coverage: z.object({
        measured: z.number().int(),
        /** Mean share of dish weight matched, 0–1. Null when nothing was measured. */
        mean: z.number().nullable(),
        /** Below the threshold the product itself refuses to estimate on. */
        belowThreshold: z.number().int(),
        threshold: z.number(),
    }),
    /** Busiest first. The catalog gaps worth closing next. */
    unresolvedIngredients: z.array(adminAiUnresolvedIngredientSchema),
    /** Worst first. The dishes whose portions the model reads most wrongly. */
    portionOffenders: z.array(adminAiPortionOffenderSchema),
});
// --- Memory extraction ------------------------------------------------------
export const adminAiMemorySchema = z.object({
    windowDays: z.number().int(),
    from: z.string(),
    memories: z.object({
        /** Written in the window. */
        created: z.number().int(),
        /** Superseded in the window — a later fact contradicted them. */
        superseded: z.number().int(),
        /** Standing facts, all time. Not windowed: the coach reads all of them. */
        active: z.number().int(),
        /** Human-entered corrections, as opposed to extracted from a session. */
        manual: z.number().int(),
    }),
    /**
     * Memories with no embedding yet. Written by a background job, so a number
     * that only grows means the backfill has stopped and similarity search is
     * silently missing recent facts.
     */
    awaitingEmbedding: z.number().int(),
    /** The queue that does the extracting, read from BullMQ. */
    queue: z.object({
        name: z.literal('memory-extraction'),
        isPaused: z.boolean(),
        counts: queueCountsSchema,
    }),
});
// --- Spend by user ----------------------------------------------------------
/**
 * Who the model bill belongs to.
 *
 * `LlmCall.userId` makes this a measured figure rather than a guess. Three
 * kinds of call carry no user and are reported separately rather than spread
 * over whoever is visible: work written once and read by everyone (a recipe
 * guide), the offline catalog classifiers, and every row from before the
 * column existed. Saying "this much belongs to nobody in particular" is the
 * whole difference between a number an operator can act on and a plausible one.
 */
export const adminAiSpendByUserQuerySchema = adminAiWindowQuerySchema.extend({
    /** A ranking, not a page: the sort key is an aggregate that moves between requests. */
    limit: z.coerce.number().int().min(1).max(100).default(20),
    sort: z.enum(['cost', 'calls']).default('cost'),
    entitlement: z.enum(['free', 'premium']).optional(),
    /** Part of a name or phone number. Narrows the rows, not the cards' totals. */
    search: z.string().trim().max(120).optional(),
});
export const adminAiUserSpendSchema = z.object({
    userId: z.string().uuid(),
    displayName: z.string().nullable(),
    phone: z.string(),
    entitlement: z.enum(['free', 'premium']),
    calls: z.number().int(),
    /**
     * Plan generations started for this person in the window — the same count
     * as the AI cost tab's, including a run the solver finished without a model
     * call and each retry of a failed one.
     */
    planRuns: z.number().int(),
    /** Their most recent model call in the window. */
    lastCallAt: z.string(),
    /** Null when nothing this person ran reported a cost at all. */
    costUsd: z.number().nullable(),
    /** True when a call of theirs succeeded or timed out without a cost — a floor, not a total. */
    costIsPartial: z.boolean(),
    /**
     * The average over the calls that reported a cost. Failures and timeouts are
     * left out of the divisor: they would drag the average toward zero. Null
     * when no call reported one.
     */
    costPerCallUsd: z.number().nullable(),
    /** What they spend most on. Null when nothing they ran reported a cost. */
    topTask: z.enum(LLM_TASKS).nullable(),
});
/**
 * What no person owns.
 *
 * Kept as its own block rather than folded into a "everyone else" row: those
 * are two different facts, and a row called "unattributed" in a ranking of
 * people reads as a person.
 */
export const adminAiUnattributedSchema = z.object({
    calls: z.number().int(),
    costUsd: z.number().nullable(),
    /** True when a call in it succeeded or timed out without reporting a cost. */
    costIsPartial: z.boolean(),
    /**
     * Calls that predate `llm_calls.user_id`. They can never be attributed —
     * there is no rule that could invent whose they were — and they age out with
     * the retention window rather than being backfilled.
     */
    beforeAttribution: z.number().int(),
});
/**
 * Everyone the ranking covers, not just the rows it returns: the same
 * entitlement filter, no limit. `users` is how many people had any call.
 */
export const adminAiAttributedSchema = z.object({
    users: z.number().int(),
    calls: z.number().int(),
    costUsd: z.number().nullable(),
    costIsPartial: z.boolean(),
});
export const adminAiSpendByUserSchema = z.object({
    windowDays: z.number().int(),
    from: z.string(),
    /** The whole bill: every call in the window, whoever it belongs to. */
    totals: adminAiSpendSchema,
    attributed: adminAiAttributedSchema,
    /** Ranked, longest first by whichever key was asked for. */
    users: z.array(adminAiUserSpendSchema),
    unattributed: adminAiUnattributedSchema,
});
// --- One person's spend -----------------------------------------------------
/** One plan generation, with what the model cost to produce it. */
export const adminAiUserPlanRunSchema = z.object({
    runId: z.string().uuid(),
    /** Empty on a run that failed before it minted a week. */
    weekStartDate: z.string(),
    status: z.enum(PLAN_RUN_STATUSES),
    calls: z.number().int(),
    costUsd: z.number().nullable(),
    costIsPartial: z.boolean(),
});
export const adminAiUserTaskSpendSchema = z.object({
    task: z.enum(LLM_TASKS),
    calls: z.number().int(),
    costUsd: z.number().nullable(),
    /** True when a call of this task succeeded or timed out without a cost. */
    costIsPartial: z.boolean(),
    /** This task's share of this person's cost, 0-1. Null when they cost nothing. */
    share: z.number().nullable(),
});
export const adminUserAiSpendSchema = z.object({
    windowDays: z.number().int(),
    from: z.string(),
    totals: z.object({
        calls: z.number().int(),
        costUsd: z.number().nullable(),
        costIsPartial: z.boolean(),
        inputTokens: z.number().int(),
        outputTokens: z.number().int(),
    }),
    /** Their share of the window's whole model bill, 0-1. Null when it cost nothing. */
    shareOfWindow: z.number().nullable(),
    /** One row per task they ran, dearest first. Tasks they never ran are absent. */
    tasks: z.array(adminAiUserTaskSpendSchema),
    /**
     * Every plan generation started in the window. `planRuns` lists only the
     * newest few, so a count taken from its length stops at the cap.
     */
    planRunCount: z.number().int(),
    /** Measured per run, newest first, at most a dozen. */
    planRuns: z.array(adminAiUserPlanRunSchema),
});
//# sourceMappingURL=ai-ops.js.map